import type {
  UnreadNotificationCountResponse,
  MarkNotificationReadResponse,
  PaginatedNotificationsResponse,
  NotificationCategory,
} from '@mrb91002/shared-types'

import { secureFetch, SECURE_API_BASE } from './helpers'

/**
 * Get per-category unread notification counts for badge and nav
 * GET /notifications/unread-count
 */
export async function getUnreadNotificationCount(idToken: string): Promise<UnreadNotificationCountResponse> {
  return secureFetch(`${SECURE_API_BASE}/notifications/unread-count`, idToken, { method: 'GET' })
}

/**
 * Fetch paginated notifications — all or by category
 * GET /notifications
 * GET /notifications?category=patterns
 * GET /notifications?category=patterns&lastKey=...
 */
export async function fetchNotificationsPaginated(
  lastKey: string | null,
  idToken: string,
  category?: NotificationCategory,
): Promise<PaginatedNotificationsResponse> {
  const params = new URLSearchParams()
  if (category && category !== 'all') params.set('category', category)
  if (lastKey) params.set('lastKey', lastKey)
  const qs = params.toString()
  const url = qs ? `${SECURE_API_BASE}/notifications?${qs}` : `${SECURE_API_BASE}/notifications`
  return secureFetch(url, idToken, { method: 'GET' })
}

/**
 * Mark a single notification as read
 * PATCH /notifications/{id}/read
 * Body: { id, category, created_at_ms }
 */
export async function markNotificationRead(
  notificationId: string,
  category: Exclude<NotificationCategory, 'all'>,
  created_at_ms: number,
  idToken: string,
): Promise<MarkNotificationReadResponse> {
  return secureFetch<MarkNotificationReadResponse>(`${SECURE_API_BASE}/notifications/${encodeURIComponent(notificationId)}/read`, idToken, {
    method: 'PATCH',
    body: JSON.stringify({ id: notificationId, category, created_at_ms }),
  })
}

/**
 * Delete a notification
 * DELETE /notifications/{id}?category=patterns&created_at_ms=1773022093765
 */
export async function deleteNotification(
  id: string,
  category: Exclude<NotificationCategory, 'all'>,
  created_at_ms: number,
  idToken: string,
): Promise<void> {
  const params = new URLSearchParams({ category, created_at_ms: String(created_at_ms) })
  return secureFetch(
    `${SECURE_API_BASE}/notifications/${encodeURIComponent(id)}?${params.toString()}`,
    idToken,
    { method: 'DELETE' },
  )
}
